import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { Address, AddressSelector, PlaceType } from '../../selectors/AddressSelector';
import LocalPostOfficeIcon from '@material-ui/icons/LocalPostOffice';
import { POBoxTypeSelector } from '../../../lookupConfiguration/selectors/POBoxTypeSelector';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('ClientPostalAddressPanel');

// Explicit request (EnquiryFormDrawer split): the client's "Postal Address" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface ClientPostalAddressPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    homeCountry: string;
    isPoBoxRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

interface ClientPostalAddressPanelBodyProps extends ClientPostalAddressPanelProps {
    formValues: AnyObject;
    isPoBoxRequiredForValues: boolean;
}

const ClientPostalAddressPanelBody: React.FC<ClientPostalAddressPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, homeCountry, requiredValidator, createAddressSelectionHandler, createInputChangedHandler, onExpandChange,
        formValues,
        isPoBoxRequiredForValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            expanded={isExpanded}
            onChange={onExpandChange}
        // disabled={disabledAddress(values)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="client-postal-address-content"
                id="client-postal-address-header"
                className={classes.AccordionSummary}
            >
                <Typography className={classes.heading}>
                    <LocalPostOfficeIcon className={classes.headingIcon} />
                    Postal Address
                </Typography>

                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {/* {getAddressLabelForPanel} */}
                            {values.pOBoxType ? `${values.pOBoxType} ` : ''}
                            {values.pOBox ? `${values.pOBox}, ` : ''}
                            {values.postalSuburb ? `${values.postalSuburb}, ` : ''}
                            {values.postalState ? `${values.postalState}, ` : ''}
                            {values.postalPostCode ? `${values.postalPostCode}, ` : ''}
                            {values.postalCountry ? `${values.postalCountry}` : ''}
                        </div>
                    </div>
                </div>

            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid
                        item={true}
                        xs={4}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="pOBoxType"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <POBoxTypeSelector
                                    variant="outlined"
                                    size="small"
                                    // {...input}
                                    value={input.value ? { label: input.value, value: input.value } : null}
                                    label="PO Box Type"
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    // tslint:disable-next-line: max-line-length
                                    onSelection={(selection: IAutoCompleteItem, name: string) => {
                                        input.onChange(selection ? selection.value : null);
                                    }}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Field is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={8} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="pOBox"
                            subscription={{ touched: true, error: true, value: true }}
                            validate={isPoBoxRequiredForValues ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Number"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    required={isPoBoxRequiredForValues ? true : false}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Number is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="postalSuburb"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Suburb"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Suburb is required'
                                            : ''
                                    }
                                    placeTypes={['(cities)']}
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'postalSuburb')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="postalState"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="State"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'State is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="postalPostCode"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Postal Code"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Postal Code is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="postalCountry"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Country"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    // disabled={props.sourceSystem?.toLowerCase() === 'leap'}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Country is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
ClientPostalAddressPanelBody.displayName = 'ClientPostalAddressPanelBody';

export const ClientPostalAddressPanel: React.FC<ClientPostalAddressPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <ClientPostalAddressPanelBody
                {...panelProps}
                isPoBoxRequiredForValues={panelProps.isPoBoxRequired(values)}
                formValues={{
                    pOBoxType: values.pOBoxType,
                    pOBox: values.pOBox,
                    postalSuburb: values.postalSuburb,
                    postalState: values.postalState,
                    postalPostCode: values.postalPostCode,
                    postalCountry: values.postalCountry,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
ClientPostalAddressPanel.displayName = 'ClientPostalAddressPanel';
